import Sidebar from "@/components/Home_Sidebar/sidebarView.vue";
import Navbar from "@/components/Home_Navbar/NavbarView.vue";

const assistant_reply = Object.freeze({
  lines: [
    "I've reviewed the workspace context and found three useful next steps:",
    "Add POPIA-specific retention language, confirm the review owner, and flag cross-border processing for compliance review."
  ],
  context: "Playground_Implementation_v2.pdf",
  actions: ["Add POPIA language", "Export as DOCX", "Flag for review"]
});

const default_context = Object.freeze({
  name: "Playground_Implementation_v2.pdf",
  source: "Documents"
});

const ai_sources = Object.freeze([
  { name: "Documents", count: 12, icon: "far fa-file-alt" },
  { name: "Projects", count: 4, icon: "fas fa-diagram-project" },
  { name: "Governance", count: 7, icon: "fas fa-shield-halved" }
]);

const quick_actions = Object.freeze(["Add POPIA language", "Export as DOCX", "Flag for review"]);

const initial_chats = Object.freeze([
  {
    id: "project-scoping",
    group: "Pinned",
    category: "reviews",
    pinned: true,
    title: "Project Scoping Help",
    preview: "Can you summarize ...",
    time: "now",
    messages: [
      {
        id: 1,
        sender: "ai",
        lines: [
          "Hi Gabrielle! I'm Ziva. I have context about your workspace, documents, and projects. How can I help you today?"
        ],
        time: "09:30 AM"
      },
      {
        id: 2,
        sender: "user",
        lines: ["Can you summarise the Playground Implementation doc and flag any compliance risks?"],
        time: "09:31 AM"
      },
      {
        id: 3,
        sender: "ai",
        lines: [
          "I've reviewed Playground_Implementation_v2.pdf from your Documents workspace.",
          "Key risk: the retention policy allows up to 90 days unless a controller specifies otherwise. Confirm whether POPIA retention and deletion obligations are explicitly mapped."
        ],
        code: "3.1 Data Retention Policy\n\nAll data processed within the Playground environment shall be retained for a maximum of 90 days from ingestion, unless otherwise specified by the data controller. Audit logs of all deletion events must be maintained for 12 months.",
        context: "Playground_Implementation_v2.pdf",
        actions: ["Add POPIA language", "Export as DOCX", "Flag for review"],
        time: "09:33 AM"
      }
    ]
  },
  {
    id: "document-drafting",
    group: "Today",
    category: "sources",
    title: "Document Drafting",
    preview: "Sure, here's the draft...",
    time: "2h",
    messages: [
      {
        id: 1,
        sender: "ai",
        lines: ["I can help draft, refine, or convert workspace notes into a document-ready format."],
        time: "08:12 AM"
      }
    ]
  },
  {
    id: "data-analysis",
    group: "Today",
    category: "tasks",
    title: "Data Analysis Q3",
    preview: "The trend shows a 12...",
    time: "5h",
    messages: [
      {
        id: 1,
        sender: "ai",
        lines: ["The Q3 trend suggests a 12 percent lift in active workspace usage, led by document collaboration."],
        time: "06:30 AM"
      }
    ]
  },
  {
    id: "compliance-review",
    group: "Yesterday",
    category: "reviews",
    title: "Compliance Review",
    preview: "Based on section 4.2...",
    time: "1d",
    messages: [
      {
        id: 1,
        sender: "ai",
        lines: ["Based on section 4.2, the strongest control gap is the missing approval trail for exception handling."],
        time: "Yesterday"
      }
    ]
  },
  {
    id: "email-templates",
    group: "Yesterday",
    category: "tasks",
    title: "Email Templates",
    preview: "Here are 3 variations...",
    time: "1d",
    messages: [
      {
        id: 1,
        sender: "ai",
        lines: ["Here are three variations: concise, formal, and customer-friendly."],
        time: "Yesterday"
      }
    ]
  }
]);

function clone_chat(chat) {
  return {
    ...chat,
    messages: chat.messages.map(message => ({ ...message }))
  };
}

export default {
  name: "ZivaView",
  components: { Sidebar, Navbar },

  data: () => ({
    search_query: "",
    active_chat_id: null,
    draft: "",
    is_typing: false,
    show_mobile_list: false,
    ai_list_collapsed: false,
    active_chat_menu_id: null,
    active_context: { ...default_context },
    ai_sources: ai_sources.map(source => ({ ...source })),
    quick_actions: [...quick_actions],
    chats: initial_chats.map(clone_chat)
  }),

  computed: {
    active_chat() {
      return this.chats.find((chat) => chat.id === this.active_chat_id) || null;
    },

    filtered_chats() {
      let chats = this.chats;

      const query = this.search_query.trim().toLowerCase();
      if (!query) return [...chats].sort(this.sort_chats);
      return chats.filter((chat) => (
        chat.title.toLowerCase().includes(query)
        || chat.preview.toLowerCase().includes(query)
      )).sort(this.sort_chats);
    },

  },

  mounted() {
    if (this.active_chat_id) {
      this.scroll_to_bottom();
    }
  },

  methods: {
    select_chat(chat_id) {
      this.active_chat_id = chat_id;
      this.active_chat_menu_id = null;
      this.$nextTick(this.scroll_to_bottom);
    },

    toggle_ai_list() {
      this.ai_list_collapsed = !this.ai_list_collapsed;
      this.active_chat_menu_id = null;
    },

    sort_chats(a, b) {
      if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
      return 0;
    },

    toggle_chat_menu(chat_id) {
      this.active_chat_menu_id = this.active_chat_menu_id === chat_id ? null : chat_id;
    },

    toggle_pin_chat(chat_id) {
      const chat = this.chats.find((item) => item.id === chat_id);
      if (!chat) return;
      chat.pinned = !chat.pinned;
      this.active_chat_menu_id = null;
    },

    rename_chat(chat_id) {
      const chat = this.chats.find((item) => item.id === chat_id);
      if (!chat) return;
      const name = window.prompt("Rename chat", chat.title);
      if (!name || !name.trim()) {
        this.active_chat_menu_id = null;
        return;
      }
      chat.title = name.trim();
      this.active_chat_menu_id = null;
    },

    delete_chat(chat_id) {
      const chat = this.chats.find((item) => item.id === chat_id);
      if (!chat || !window.confirm(`Delete "${chat.title}"?`)) return;
      this.chats = this.chats.filter((item) => item.id !== chat_id);
      if (this.active_chat_id === chat_id) {
        this.active_chat_id = this.filtered_chats[0]?.id || null;
      }
      this.active_chat_menu_id = null;
    },

    start_new_chat(starter_prompt = "") {
      if (typeof starter_prompt !== "string") {
        starter_prompt = "";
      }
      const id = `new-${Date.now()}`;
      const has_starter_prompt = Boolean(starter_prompt);
      this.chats.unshift({
        id,
        group: "Today",
        category: "tasks",
        pinned: false,
        title: "New chat",
        preview: starter_prompt || "Ask anything...",
        time: "now",
        messages: has_starter_prompt ? [
          {
            id: 1,
            sender: "ai",
            lines: [
              "I'm ready. Ask about a document, project, governance item, or workspace activity.",
              "I can use your AIC One context to summarise, draft, compare, or flag items for review."
            ],
            actions: ["Add POPIA language", "Export as DOCX", "Flag for review"],
            time: this.current_time()
          }
        ] : []
      });
      this.active_chat_id = id;
      this.search_query = "";
      this.draft = starter_prompt;
      this.ai_list_collapsed = false;
      this.$nextTick(() => {
        this.resize_composer();
        this.scroll_to_bottom();
        this.$refs.composer?.focus();
      });
    },

    start_chat_from_draft() {
      const prompt = this.draft.trim();
      if (!prompt || this.is_typing) return;
      this.start_new_chat();
      this.$nextTick(() => {
        this.draft = prompt;
        this.resize_composer();
        this.send_message();
      });
    },

    send_message() {
      if (!this.active_chat || !this.draft || this.is_typing) return;
      const content = this.draft;
      const chat = this.active_chat;
      chat.messages.push({
        id: Date.now(),
        sender: "user",
        lines: [content],
        time: this.current_time()
      });
      chat.preview = content;
      chat.time = "now";
      this.draft = "";
      this.reset_composer_height();
      this.is_typing = true;
      this.$nextTick(this.scroll_to_bottom);

      window.setTimeout(() => {
        chat.messages.push({
          id: Date.now() + 1,
          sender: "ai",
          time: this.current_time(),
          ...assistant_reply
        });
        chat.preview = "I've reviewed the workspace context...";
        this.is_typing = false;
        this.$nextTick(this.scroll_to_bottom);
      }, 650);
    },

    apply_suggestion(action) {
      const prompts = {
        "Add POPIA language": "Add POPIA-specific language to the retention section.",
        "Export as DOCX": "Prepare this answer as a DOCX-ready executive summary.",
        "Flag for review": "Flag this item for compliance review and list the reason."
      };
      this.draft = Reflect.get(prompts, action) || action;
      this.$nextTick(() => {
        this.resize_composer();
        this.$refs.composer?.focus();
      });
    },

    action_icon(action) {
      if (action.includes("Export")) return "far fa-file-word";
      if (action.includes("Flag")) return "far fa-flag";
      return "fas fa-plus";
    },

    resize_composer() {
      const el = this.$refs.composer;
      if (!el) return;
      el.style.height = "auto";
      el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
    },

    resize_home_composer() {
      const el = this.$refs.home_composer;
      if (!el) return;
      el.style.height = "auto";
      el.style.height = `${Math.min(el.scrollHeight, 150)}px`;
    },

    reset_composer_height() {
      this.$nextTick(() => {
        const el = this.$refs.composer;
        if (el) el.style.height = "auto";
      });
    },

    scroll_to_bottom() {
      this.$nextTick(() => {
        const el = this.$refs.message_scroller;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },

    current_time() {
      return new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }
  }
};
